// Leave Approval Filter Types
// Reference: Leave_approval_filter.md

export interface LeaveApprovalFilters {
  // Month filter - single month selector, filters by start_date
  // Special values: 'all' for all time, 'recent' for most recent requests
  month: string | null; // Format: 'YYYY-MM', 'all', 'recent', or null

  // Employee filter - multi-select, moved from existing filter
  employeeIds: string[];

  // Leave type filter - multi-select, derived from data
  leaveTypes: string[];

  // Status filter - multi-select, default all
  statuses: string[];
}

// Special filter values
export const SPECIAL_MONTH_VALUES = {
  ALL_TIME: 'all',
  MOST_RECENT: 'recent',
} as const;

export const DEFAULT_LEAVE_APPROVAL_FILTERS: LeaveApprovalFilters = {
  month: null, // Will be set to current month at runtime
  employeeIds: [],
  leaveTypes: [],
  statuses: [], // Empty means all
};

// Available statuses (current + future-proofed per requirements)
export const LEAVE_REQUEST_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type LeaveRequestStatus = typeof LEAVE_REQUEST_STATUSES[number]['value'];

// Helper to generate month options: past 12 months + current + next 6 future months
export const generateMonthOptions = (): { value: string; label: string; group?: string }[] => {
  const options: { value: string; label: string; group?: string }[] = [];
  const now = new Date();
  
  // Add special options first
  options.push({ 
    value: SPECIAL_MONTH_VALUES.MOST_RECENT, 
    label: 'Most Recent (20)',
    group: 'quick'
  });
  options.push({ 
    value: SPECIAL_MONTH_VALUES.ALL_TIME, 
    label: 'All Time',
    group: 'quick'
  });
  
  // Add future months (next 6 months, excluding current)
  for (let i = 6; i >= 1; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label, group: 'future' });
  }
  
  // Add current month and past 12 months
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label, group: i === 0 ? 'current' : 'past' });
  }
  
  return options;
};

// Get current month in YYYY-MM format
export const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Check if a month value is a special filter
export const isSpecialMonthValue = (value: string | null): boolean => {
  return value === SPECIAL_MONTH_VALUES.ALL_TIME || value === SPECIAL_MONTH_VALUES.MOST_RECENT;
};

// Helper to count active filters
export const countActiveFilters = (filters: LeaveApprovalFilters): number => {
  let count = 0;
  // Count month as active if it's set and not the default (current month)
  if (filters.month && filters.month !== getCurrentMonth()) count++;
  if (filters.employeeIds.length > 0) count++;
  if (filters.leaveTypes.length > 0) count++;
  if (filters.statuses.length > 0) count++;
  return count;
};
